import { useCallback, useEffect, useMemo, useState } from 'react';
import { rbacService } from '../services/rbacService';
import { EMPTY_GRANTS } from '../utils/rbacConstants';
import { mergeGrantsWithCatalog } from '../utils/rbacMappers';
import { applyNodeGrantPatch, buildPermissionTreeSections } from '../utils/permissionTreeUtils';
import { syncBusinessFunctionsObjectPolicies, BUSINESS_FUNCTIONS_MODULE_CODENAME } from '../utils/businessFunctionsPermissionUtils';
import {
  applyObjectGrantSideEffects,
  normalizeObjectGrantSideEffects,
} from '../utils/objectPermissionRules';
import {
  applyFeatureGrantSideEffects,
  normalizeFeatureGrantSideEffects,
} from '../utils/featurePermissionRules';

function normalizeSubmitGrants(grants) {
  return normalizeFeatureGrantSideEffects(
    normalizeObjectGrantSideEffects(syncBusinessFunctionsObjectPolicies(grants))
  );
}

export default function useProfileClonePreview(profileId, open) {
  const [catalog, setCatalog] = useState(null);
  const [grants, setGrants] = useState(EMPTY_GRANTS);
  const [resourceTree, setResourceTree] = useState({ accounts: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const load = useCallback(async () => {
    if (!profileId) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const previewRes = await rbacService.getClonePreview(profileId);
      if (!previewRes.ok) {
        setCatalog(null);
        setGrants(EMPTY_GRANTS);
        setResourceTree({ accounts: [] });
        setError('Unable to load permissions.');
        return;
      }

      const payload = previewRes.data || {};
      const nextCatalog = payload.catalog || null;
      const profilePermissions = payload.profile?.permissions || EMPTY_GRANTS;

      setCatalog(nextCatalog);
      setGrants(mergeGrantsWithCatalog(nextCatalog, profilePermissions));
      setResourceTree(payload.resource_tree || { accounts: [] });
      setIsDirty(false);
    } catch {
      setCatalog(null);
      setGrants(EMPTY_GRANTS);
      setResourceTree({ accounts: [] });
      setError('Unable to load permissions.');
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (!open || !profileId) {
      setCatalog(null);
      setGrants(EMPTY_GRANTS);
      setResourceTree({ accounts: [] });
      setError('');
      setIsDirty(false);
      setIsLoading(false);
      return;
    }

    load();
  }, [load, open, profileId]);

  const updateFeatureGrant = useCallback((codename, granted) => {
    setGrants((prev) => applyFeatureGrantSideEffects(codename, granted, prev));
    setIsDirty(true);
  }, []);

  const updateObjectGrant = useCallback((model, action, granted) => {
    setGrants((prev) => applyObjectGrantSideEffects(model, action, granted, prev));
    setIsDirty(true);
  }, []);

  const updateNodeGrants = useCallback((node, granted) => {
    setGrants((prev) => applyNodeGrantPatch(prev, node, granted));
    setIsDirty(true);
  }, []);

  const patchGrants = useCallback((nextGrants) => {
    setGrants((prev) => {
      const merged = {
        ...prev,
        features: nextGrants.features ?? prev.features,
        objects: nextGrants.objects ?? prev.objects,
        resources: nextGrants.resources ?? prev.resources,
      };
      return nextGrants.resources !== undefined
        ? syncBusinessFunctionsObjectPolicies(merged)
        : merged;
    });
    setIsDirty(true);
  }, []);

  const getSubmitGrants = useCallback(() => normalizeSubmitGrants(grants), [grants]);

  const businessFunctionsSection = useMemo(
    () => buildPermissionTreeSections(catalog).find(
      (section) => section.codename === BUSINESS_FUNCTIONS_MODULE_CODENAME
    ),
    [catalog]
  );

  const hasPermissionsContent = useMemo(
    () => Boolean(
      buildPermissionTreeSections(catalog).some(
        (section) => section.codename !== BUSINESS_FUNCTIONS_MODULE_CODENAME
      ) || businessFunctionsSection
    ),
    [businessFunctionsSection, catalog]
  );

  const canSubmitPermissions = !isLoading && !error && hasPermissionsContent;

  return {
    catalog,
    grants,
    resourceTree,
    businessFunctionsSection,
    hasPermissionsContent,
    canSubmitPermissions,
    isLoading,
    isDirty,
    error,
    reload: load,
    getSubmitGrants,
    updateFeatureGrant,
    updateObjectGrant,
    updateNodeGrants,
    patchGrants,
  };
}
